import { Vector3 } from "three";
import { PointCloud, PointCloudManager } from ".";
import { toast } from "../../../app";
import { t } from "../../../localization";
import { readFileJSON } from "../file-system-loader";
import { PointCloudNode } from "./point-cloud";

/** Base class for streamable cloud node */
export class StreamablePointCloudNode extends PointCloudNode {
  public cloud: StreamablePointCloud;
  public parent: StreamablePointCloudNode;
  protected level;

  constructor(name, level, center, size) {
    super();

    this.name = name;
    this.level = level;
    this.size = size;
    this.nodeCenter = center;
  }

  get shift() {
    return this.cloud.shift;
  }

  getChildOffset(index) {
    console.warn("Not implemented");
    return {x: 0, y: 0, z: 0};
  }

  addChildNode(node) {
    this.children.push(node);

    // Make sure child nodes are sorted by index
    this.children.sort((a,b) => {
      let indexA = parseInt(a.name.slice(-1));
      let indexB = parseInt(b.name.slice(-1));
      return indexA - indexB;
    });
  }

  getChildNode(index) {
    const childName = `${this.name}${index}`;
    const childSize = this.size / 2.0;
    const childLevel = this.level + 1;

    const {x,y,z} = this.getChildOffset(index);

    const childCenter = new Vector3(
      this.nodeCenter.x + (x - 0.5) * childSize,
      this.nodeCenter.y + (y - 0.5) * childSize,
      this.nodeCenter.z + (z - 0.5) * childSize
    );

    const NodeConstructor = this.constructor as any;
    return new NodeConstructor(this.cloud, this, childName,
      childLevel, childCenter, childSize);
  }
}

/** Base class for streamable cloud */
export class StreamablePointCloud extends PointCloud {
  public loaded = false;
  public loading = false;
  public spacing = null;
  protected structure = null;
  private _shift;

  constructor(pointclouds: PointCloudManager) {
    super(pointclouds);
  }

  get shift() {
    return this._shift;
  }

  set shift(value) {
    this._shift = value;
  }

  get size() {
    return this.root.size;
  }

  get dataDir() {
    return this.structure.path;
  }

  get saved() {
    return this.structure.saved;
  }

  get metadataPath() {
    console.warn("Not implemented");
    return "";
  }

  async load(structure) {
    this.structure = structure;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setName(structure.name);
    this.setVisibility(structure.visible);
    this.setIdentifier(structure.identifier);
  }

  async loadMetadata() {
    this.loading = true;

    try {
      // Read index data
      const metadata = await readFileJSON(this.metadataPath);
      this.decodeMetadata(metadata);
    } catch(err) {
      this.error = true;
      console.error(err);
      toast.error(t("toast.points-load-error", {
        name: this.structure.name
      }));
    } finally {
      this.loaded = true;
      this.loading = false;
    }
  }

  decodeMetadata(data) {
    console.warn("Not implemented");
  }

  getVisibleNodes(condition?): PointCloudNode[] {
    let visibleNodes = [];
    if (!this.root) return visibleNodes;

    let stack = [this.root];
    while (stack.length > 0) {
      let node = stack.shift();

      if (!node.visible) continue;
      if (condition && !condition(node)) continue;

      visibleNodes.push(node);
      node.children.forEach(child => {
        stack.push(child);
      });
    }

    return visibleNodes;
  }

  hideDescendants() {
    if (!this.root) return;

    let stack = [this.root];

    while (stack.length > 0) {
      let object = stack.shift();
      if (!object.visible) {
        continue;
      }

      object.setVisibility(false, false);
      object.children.forEach(child => {
        stack.push(child);
      });
    }
  }
}
